/**
 * AssignToJobModal organism — searchable job picker modal for assigning a talent to a job.
 *
 * Uses the Dialog organism (PATTERNS.md SendInterviewConfirmationDialog analog).
 * Props: { open, talentId, currentAssignments, onClose, onAssign, isPending? }
 *
 * Behavior:
 * - Renders a SearchInput that filters useJobs() list accent-insensitively by title
 * - Each job row shows: title (body-md) + StatusPill of the job status
 * - Selecting a row highlights it and enables the "Assign" CTA
 * - A job already in currentAssignments renders disabled with the "Already assigned" label
 * - NO cancel button — dismiss only via Escape or backdrop click (UI-SPEC § Modal dismiss)
 * - On Assign: calls onAssign(jobId)
 *
 * i18n keys used: talentPool.assignModal.title, talentPool.assignModal.searchPlaceholder,
 *   talentPool.assignModal.alreadyAssigned, talentPool.assignModal.assignButton
 * No cancel key — intentionally absent per UI-SPEC. Dismiss via Escape/backdrop only.
 *
 * No hardcoded strings, no gradients, no emojis.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../Dialog';
import { Button } from '../../atoms/Button';
import { SearchInput } from '../../search-input';
import { StatusPill } from '../../atoms/StatusPill';
import { getJobStatusVariant } from '../../atoms/StatusPill/statusPill.utils';
import { Typography } from '../../atoms/Typography';
import { useJobs } from '@/hooks/useTalentPool';
import { normalizeAccents } from '@/services/talentPool.utils';
import type { TalentJobAssignment } from '@/types/talent.types';

export interface AssignToJobModalProps {
  open: boolean;
  talentId: string;
  currentAssignments: TalentJobAssignment[];
  onClose: () => void;
  onAssign: (jobId: string) => void;
  isPending?: boolean;
}

export const AssignToJobModal: React.FC<AssignToJobModalProps> = ({
  open,
  currentAssignments,
  onClose,
  onAssign,
  isPending = false,
}) => {
  const { t } = useTranslation('talentPool');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const { data: jobs = [], isLoading } = useJobs();

  const assignedJobIds = useMemo(
    () => new Set(currentAssignments.map((a) => a.jobId)),
    [currentAssignments],
  );

  const filteredJobs = useMemo(() => {
    if (!searchQuery.trim()) return jobs;
    const normalized = normalizeAccents(searchQuery.trim());
    return jobs.filter((job) => normalizeAccents(job.title).includes(normalized));
  }, [jobs, searchQuery]);

  const handleClose = () => {
    setSearchQuery('');
    setSelectedJobId(null);
    onClose();
  };

  const handleAssign = () => {
    if (!selectedJobId) return;
    onAssign(selectedJobId);
    setSearchQuery('');
    setSelectedJobId(null);
  };

  const isAssignDisabled = !selectedJobId || isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}
      title={t('assignModal.title')}
      maxWidth="md"
      closeOnBackdropClick
    >
      <Dialog.Header title={t('assignModal.title')} showCloseButton onClose={handleClose} />

      <Dialog.Body>
        <div className="flex flex-col gap-4">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('assignModal.searchPlaceholder')}
            showClearButton
            className="m-0.5"
          />

          <div className="flex flex-col divide-y divide-light-lila/40 max-h-72 overflow-y-auto">
            {isLoading && (
              <div className="py-6 text-center">
                <Typography variant="body-sm" color="secondary">
                  {t('pool.searchPlaceholder')}
                </Typography>
              </div>
            )}
            {!isLoading && filteredJobs.length === 0 && (
              <div className="py-6 text-center">
                <Typography variant="body-sm" color="secondary">
                  {t('pool.emptyHeading')}
                </Typography>
              </div>
            )}
            {filteredJobs.map((job) => {
              const isAlreadyAssigned = assignedJobIds.has(job.id);
              const isSelected = selectedJobId === job.id;

              return (
                <button
                  key={job.id}
                  type="button"
                  disabled={isAlreadyAssigned}
                  onClick={() => !isAlreadyAssigned && setSelectedJobId(job.id)}
                  className={[
                    'flex items-center gap-3 px-3 py-3 text-left w-full transition-colors rounded-md',
                    isAlreadyAssigned
                      ? 'opacity-50 cursor-not-allowed'
                      : isSelected
                        ? 'bg-purple/10 cursor-pointer'
                        : 'hover:bg-background-gray cursor-pointer',
                  ].join(' ')}
                >
                  <div className="flex-1 min-w-0">
                    <Typography variant="body-md" weight="medium" color="primary">
                      {job.title}
                    </Typography>
                    {job.businessUnit && (
                      <Typography variant="body-sm" color="secondary">
                        {job.businessUnit}
                      </Typography>
                    )}
                  </div>
                  {isAlreadyAssigned ? (
                    <Typography variant="body-sm" color="secondary">
                      {t('assignModal.alreadyAssigned')}
                    </Typography>
                  ) : (
                    <StatusPill variant={getJobStatusVariant(job.status)}>
                      {job.status}
                    </StatusPill>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </Dialog.Body>

      <Dialog.Footer align="right">
        <Button
          type="contained"
          intent="secondary"
          size="md"
          label={t('assignModal.assignButton')}
          disabled={isAssignDisabled}
          loading={isPending}
          onClick={handleAssign}
        />
      </Dialog.Footer>
    </Dialog>
  );
};

AssignToJobModal.displayName = 'AssignToJobModal';
